import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { readJsonWithOnboarding } from '../onboarding/notify-onboarding-update';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import { NOTES_CONTENT_MAX_LENGTH, stripHtml } from '../utils/note-utils';
import { wouldExceedStorageQuota } from '../utils/storage-quota';
import type { ProjectNote } from '../types/project-note';
import type { MentionableEntity } from './RichTextField';
import { NoteFormDialog } from './NoteFormDialog';

interface CreateProjectNoteDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (note: ProjectNote) => void;
}

export const CreateProjectNoteDialog: React.FC<CreateProjectNoteDialogProps> = ({
  projectId,
  open,
  onOpenChange,
  onCreated,
}) => {
  const { token } = useAuth();
  const { t } = useLocale();
  const { showToast, showErrorToast, hideToast } = useToast();
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const contentLength = stripHtml(content).length;
  const contentOk = contentLength <= NOTES_CONTENT_MAX_LENGTH;

  useEffect(() => {
    if (!open || !projectId || !token) {
      return;
    }
    const fetchMentionable = async (): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
      } catch {
        setMentionableEntities([]);
      }
    };
    void fetchMentionable();
  }, [open, projectId, token]);

  const resetForm = (): void => {
    setContent('');
    setFile(null);
  };

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  const uploadAttachmentAsResource = async (attachment: File): Promise<string | null> => {
    if (!token) return null;
    const exceedsQuota = await wouldExceedStorageQuota(token, attachment.size);
    if (exceedsQuota) {
      window.dispatchEvent(new CustomEvent('storage-limit-reached'));
      return null;
    }
    setUploadingAttachment(true);
    try {
      const presignedRes = await fetch(`${API_BASE}/api/projects/${projectId}/resources/presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: attachment.name,
          fileSize: attachment.size,
          mimeType: attachment.type || 'application/octet-stream',
        }),
      });
      if (!presignedRes.ok) {
        const err = await presignedRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to get upload URL');
      }
      const { presignedUrl, s3Key } = await presignedRes.json();
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': attachment.type || 'application/octet-stream' },
        body: attachment,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const saveRes = await fetch(`${API_BASE}/api/projects/${projectId}/resources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: attachment.name,
          fileSize: attachment.size,
          mimeType: attachment.type || 'application/octet-stream',
          s3Key,
          fromNote: true,
        }),
      });
      if (!saveRes.ok) {
        const err = await saveRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save resource');
      }
      const saveData = await saveRes.json();
      return saveData.id;
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Upload failed');
      return null;
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!token || !contentOk) return;
    let attachmentResourceId: string | null = null;
    if (file) {
      const resourceId = await uploadAttachmentAsResource(file);
      if (resourceId) attachmentResourceId = resourceId;
      else return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content,
          ...(attachmentResourceId && { attachmentResourceId }),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create note');
      }
      const data = await readJsonWithOnboarding<{ note: ProjectNote }>(res);
      resetForm();
      onOpenChange(false);
      onCreated?.(data.note);
      window.dispatchEvent(
        new CustomEvent('note-created', {
          detail: { projectId, note: data.note },
        })
      );
      showToast(
        <>
          {t('note_created_toast_suffix_before')}
          <Link
            to={`/projects/${projectId}/notes?noteId=${encodeURIComponent(data.note.id)}`}
            style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}
            onClick={() => hideToast()}
          >
            {t('note_created_toast_link_text')}
          </Link>
          {t('note_created_toast_suffix_after')}
        </>,
      );
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to create note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <NoteFormDialog
      open={open}
      onOpenChange={handleOpenChange}
      mode="create"
      content={content}
      onContentChange={setContent}
      file={file}
      onFileChange={setFile}
      mentionableEntities={mentionableEntities}
      contentLength={contentLength}
      maxLength={NOTES_CONTENT_MAX_LENGTH}
      contentOk={contentOk}
      saving={saving}
      uploadingAttachment={uploadingAttachment}
      onSave={() => {
        void handleSave();
      }}
      onCancel={() => handleOpenChange(false)}
    />
  );
};
